<script setup lang="ts">
  import { useTemplateRef, toValue } from 'vue'
  import { isStakeholderContactPerson } from '@gat-ship/types'
  import {
    defineContactsContext,
    provideContactPerson
  } from '../../composables/useStakeholderContacts'
  import { Button } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import ContactModal from './modals/ContactModal.vue'
  import { text } from '@gat-ship/stakeholders'

  // Template renders a fragment (<slot /> + two modals), so Vue can't
  // auto-inherit fallthrough attrs onto a single root. Route-level props
  // like `id` pass through from the parent view and trigger a Vue warn
  // otherwise. Matches the pattern in stakeholder-stats-provider.vue.
  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<{
      // Both props are consumed reactively inside defineContactsContext via
      // the passed `props` object; the rule can't see through that.
      // eslint-disable-next-line vue/no-unused-properties
      stakeholderId: string
      // eslint-disable-next-line vue/no-unused-properties
      isCreate?: boolean
    }>(),
    {
      isCreate: false
    }
  )

  const modalContactRef = useTemplateRef('modalContactRef')

  const context = defineContactsContext(props, {
    contact: modalContactRef
  })

  provideContactPerson(context)

  defineExpose(context)
</script>

<template>
  <slot :contacts="toValue(context.contacts)" />
  <ContactModal ref="modalContactRef" />
  <AlertDialog v-model:open="context.commands.showDeleteConfirm.value">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ text.contactPersons.deleteContactPerson.title }}
        </AlertDialogTitle>
        <AlertDialogDescription class="whitespace-pre-line">
          {{
            context.commands.pendingDeleteContact.value
              ? text.contactPersons.deleteContactPerson.message(
                  isStakeholderContactPerson(
                    context.commands.pendingDeleteContact.value
                  )
                    ? (context.commands.pendingDeleteContact.value.firstName ??
                        '')
                    : (context.commands.pendingDeleteContact.value.otherName ??
                        '')
                )
              : ''
          }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="context.commands.onConfirmDelete()">
          Confirm
        </Button>
        <Button
          variant="outline"
          @click="context.commands.showDeleteConfirm.value = false"
        >
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
